// src/App.tsx
import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Menu,
  MenuItem,
  CssBaseline,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useMediaQuery,
  useTheme,
  Avatar,
  Tooltip,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Icons
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import MenuIcon from '@mui/icons-material/Menu';
import GrassIcon from '@mui/icons-material/Grass';
import WaterIcon from '@mui/icons-material/Water';
import EnergyIcon from '@mui/icons-material/BoltOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LandscapeIcon from '@mui/icons-material/Landscape';
import CalculateIcon from '@mui/icons-material/Calculate';
import MapIcon from '@mui/icons-material/Map';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

// Import from the new flat directory structure
import FoodDashboard from './components/flat/food/FoodDashboard';
import SoilMonitoringSimple from './components/Land/SoilMonitoringSimple';
import DietRecommendation from './components/Land/DietRecommendation';
import WaterDashboard from './components/Water/WaterDashboard';
import UserDashboard from './components/Accounts/UserDashboard';
import AdminDashboard from './components/Accounts/AdminDashboard';
import RegisterPage from './components/Accounts/RegisterPage';
import LoginPage from './components/Accounts/LoginPage';
import EnergyDashboard from './components/energy/EnergyDashboard';
import EnergyHeatmap from './components/energy/EnergyHeatmap';
import EnergyCalculator from './components/energy/EnergyCalculator';
// Import the new LandingPage
import LandingPage from './pages/LandingPage';
import CorsTest from './components/CorsTest';

// NavBar component to make conditional rendering cleaner
const NavBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Separate anchors for Land, Water, Energy, and Accounts menus
  const [anchorElLand, setAnchorElLand] = useState<null | HTMLElement>(null);
  const [anchorElWater, setAnchorElWater] = useState<null | HTMLElement>(null);
  const [anchorElEnergy, setAnchorElEnergy] = useState<null | HTMLElement>(null);
  const [anchorElAccounts, setAnchorElAccounts] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLandMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElLand(event.currentTarget);
  };

  const handleLandMenuClose = () => {
    setAnchorElLand(null);
  };

  const handleWaterMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElWater(event.currentTarget);
  };

  const handleWaterMenuClose = () => {
    setAnchorElWater(null);
  };

  const handleEnergyMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElEnergy(event.currentTarget);
  };

  const handleEnergyMenuClose = () => {
    setAnchorElEnergy(null);
  };

  const handleAccountsMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElAccounts(event.currentTarget);
  };

  const handleAccountsMenuClose = () => {
    setAnchorElAccounts(null);
  };

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          component="img"
          src="/EcoFusion_Logo.png"
          alt="EcoFusion"
          sx={{
            height: 50,
            width: 'auto',
          }}
        />
      </Box>
      <Divider />
      <List>
        <ListItem component={Link} to="/food" onClick={() => setMobileOpen(false)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <RestaurantIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Food Tracking" />
        </ListItem>
        <ListItem component={Link} to="/soil" onClick={() => setMobileOpen(false)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <LandscapeIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Soil Monitoring" />
        </ListItem>
        <ListItem component={Link} to="/diet" onClick={() => setMobileOpen(false)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <GrassIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Diet Recommendation" />
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem component={Link} to="/water" onClick={() => setMobileOpen(false)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <WaterIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Water Quality" />
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem component={Link} to="/energy-potential" onClick={() => setMobileOpen(false)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <MapIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Global Energy Potential" />
        </ListItem>
        <ListItem component={Link} to="/energy-calculator" onClick={() => setMobileOpen(false)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <CalculateIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="ROI Calculator" />
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem component={Link} to="/login" onClick={() => setMobileOpen(false)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <LoginIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Login" />
        </ListItem>
        <ListItem component={Link} to="/user" onClick={() => setMobileOpen(false)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <DashboardIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="User Dashboard" />
        </ListItem>
        <ListItem component={Link} to="/admin" onClick={() => setMobileOpen(false)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <AdminPanelSettingsIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Admin Dashboard" />
        </ListItem>
        <ListItem component={Link} to="/register" onClick={() => setMobileOpen(false)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <PersonAddIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Register Account" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar position="sticky" elevation={0}>
      <Toolbar sx={{ minHeight: { xs: '64px', md: '70px' }, px: { xs: 1, sm: 2, md: 3 } }}>
        {isMobile ? (
          <>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src="/EcoFusion_Logo.png"
                alt="EcoFusion"
                sx={{
                  height: 40,
                  width: 'auto',
                }}
              />
            </Box>
            <IconButton color="inherit" component={Link} to="/user">
              <AccountCircleIcon />
            </IconButton>
          </>
        ) : (
          <>
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: 4,
                '&:hover': {
                  opacity: 0.9,
                },
              }}
            >
              <Box
                component="img"
                src="/EcoFusion_Logo.png"
                alt="EcoFusion"
                sx={{
                  height: 50,
                  width: 'auto',
                  mr: 1,
                }}
              />
            </Box>
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
              <Button
                color="inherit"
                onClick={handleLandMenuOpen}
                sx={{
                  borderRadius: '8px',
                  py: 1,
                  px: 2,
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                }}
                startIcon={<GrassIcon />}
              >
                Land
              </Button>
              <Menu
                anchorEl={anchorElLand}
                open={Boolean(anchorElLand)}
                onClose={handleLandMenuClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    mt: 1,
                    borderRadius: '12px',
                    minWidth: '200px',
                    overflow: 'visible',
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      left: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              >
                <MenuItem component={Link} to="/food" onClick={handleLandMenuClose} sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon>
                    <RestaurantIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Food Tracking" />
                </MenuItem>
                <MenuItem component={Link} to="/soil" onClick={handleLandMenuClose} sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon>
                    <LandscapeIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Soil Monitoring" />
                </MenuItem>
                <MenuItem component={Link} to="/diet" onClick={handleLandMenuClose} sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon>
                    <GrassIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Diet Recommendation" />
                </MenuItem>
              </Menu>

              <Button
                color="inherit"
                onClick={handleWaterMenuOpen}
                sx={{
                  borderRadius: '8px',
                  py: 1,
                  px: 2,
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                }}
                startIcon={<WaterIcon />}
              >
                Water
              </Button>
              <Menu
                anchorEl={anchorElWater}
                open={Boolean(anchorElWater)}
                onClose={handleWaterMenuClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    mt: 1,
                    borderRadius: '12px',
                    minWidth: '200px',
                    overflow: 'visible',
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      left: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              >
                <MenuItem component={Link} to="/water" onClick={handleWaterMenuClose} sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon>
                    <WaterIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Water Quality" />
                </MenuItem>
              </Menu>

              <Button
                color="inherit"
                onClick={handleEnergyMenuOpen}
                sx={{
                  borderRadius: '8px',
                  py: 1,
                  px: 2,
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                }}
                startIcon={<EnergyIcon />}
              >
                Energy
              </Button>
              <Menu
                anchorEl={anchorElEnergy}
                open={Boolean(anchorElEnergy)}
                onClose={handleEnergyMenuClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    mt: 1,
                    borderRadius: '12px',
                    minWidth: '200px',
                    overflow: 'visible',
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      left: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              >
                <MenuItem component={Link} to="/energy-potential" onClick={handleEnergyMenuClose} sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon>
                    <MapIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Global Energy Potential" />
                </MenuItem>
                <MenuItem component={Link} to="/energy-calculator" onClick={handleEnergyMenuClose} sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon>
                    <CalculateIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="ROI Calculator" />
                </MenuItem>
              </Menu>

              <Button
                color="inherit"
                onClick={handleAccountsMenuOpen}
                sx={{
                  borderRadius: '8px',
                  py: 1,
                  px: 2,
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                }}
                startIcon={<AccountCircleIcon />}
              >
                Accounts
              </Button>
              <Menu
                anchorEl={anchorElAccounts}
                open={Boolean(anchorElAccounts)}
                onClose={handleAccountsMenuClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    mt: 1,
                    borderRadius: '12px',
                    minWidth: '200px',
                    overflow: 'visible',
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      left: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              >
                <MenuItem component={Link} to="/login" onClick={handleAccountsMenuClose} sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon>
                    <LoginIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Login" />
                </MenuItem>
                <MenuItem component={Link} to="/user" onClick={handleAccountsMenuClose} sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon>
                    <DashboardIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="User Dashboard" />
                </MenuItem>
                <MenuItem component={Link} to="/admin" onClick={handleAccountsMenuClose} sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon>
                    <AdminPanelSettingsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Admin Dashboard" />
                </MenuItem>
                <MenuItem component={Link} to="/register" onClick={handleAccountsMenuClose} sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon>
                    <PersonAddIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Register Account" />
                </MenuItem>
              </Menu>
            </Box>
            <Tooltip title="Your Profile">
              <IconButton
                edge="end"
                color="inherit"
                component={Link}
                to="/user"
                sx={{
                  ml: 2,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: '2px solid rgba(255, 255, 255, 0.5)'
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <AccountCircleIcon />
                </Avatar>
              </IconButton>
            </Tooltip>
          </>
        )}
      </Toolbar>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

// Define all non-landing, non-account routes for horizontal paging
const nonLandingNonAccountRoutes = [
  '/food',
  '/soil',
  '/diet',
  '/water',
  '/energy',
  '/energy-potential',
  '/energy-calculator',
];

const App: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isLandingPage = location.pathname === '/';

  const routeIndex = nonLandingNonAccountRoutes.indexOf(location.pathname);
  const isNonLandingNonAccount = routeIndex !== -1;

  // Handlers for cyclic next/previous navigation (wrap-around)
  const handlePrevPage = () => {
    if (isNonLandingNonAccount) {
      if (routeIndex > 0) {
        navigate(nonLandingNonAccountRoutes[routeIndex - 1]);
      } else {
        // wrap to last page if first page reached
        navigate(nonLandingNonAccountRoutes[nonLandingNonAccountRoutes.length - 1]);
      }
    }
  };

  const handleNextPage = () => {
    if (isNonLandingNonAccount) {
      if (routeIndex < nonLandingNonAccountRoutes.length - 1) {
        navigate(nonLandingNonAccountRoutes[routeIndex + 1]);
      } else {
        // wrap to first page if last page reached
        navigate(nonLandingNonAccountRoutes[0]);
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CssBaseline />
      {!isLandingPage && <NavBar />}

      <Box
        sx={{
          background: isLandingPage
            ? 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)'
            : theme.palette.background.default,
          backgroundAttachment: 'fixed',
          overflowX: 'hidden',
          minHeight: isLandingPage ? '100vh' : 'calc(100vh - 70px)',
          display: 'flex',
          flexDirection: 'column',
          padding: isLandingPage ? 0 : { xs: 2, sm: 3, md: 4 },
          position: 'relative',
        }}
      >
        {/* Prev/Next nav buttons for non-landing, non-account pages */}
        {isNonLandingNonAccount && (
          <>
            <Tooltip title="Previous Page" arrow>
              <IconButton
                onClick={handlePrevPage}
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  position: 'fixed',
                  top: '50%',
                  left: 16,
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: theme.shadows[3],
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
                size="large"
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Next Page" arrow>
              <IconButton
                onClick={handleNextPage}
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  position: 'fixed',
                  top: '50%',
                  right: 16,
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: theme.shadows[3],
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
                size="large"
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}

        <Container
          maxWidth={false}
          disableGutters={isLandingPage}
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: isLandingPage ? '100%' : '1600px',
            margin: '0 auto',
          }}
        >
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/food" element={<FoodDashboard />} />
            <Route path="/soil" element={<SoilMonitoringSimple />} />
            <Route path="/diet" element={<DietRecommendation />} />
            <Route path="/water" element={<WaterDashboard />} />
            <Route path="/energy" element={<Navigate to="/energy-potential" replace />} />
            <Route path="/energy-potential" element={<EnergyHeatmap />} />
            <Route path="/energy-calculator" element={<EnergyCalculator />} />
            <Route path="/user" element={<UserDashboard />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/cors-test" element={<CorsTest />} />
          </Routes>
        </Container>

        {!isLandingPage && (
          <Box
            component="footer"
            sx={{
              p: { xs: 2, sm: 3 },
              mt: 'auto',
              backgroundColor: '#263238',
              color: 'white',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Container maxWidth="lg">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'center', md: 'flex-start' },
                  textAlign: { xs: 'center', md: 'left' },
                  gap: 2,
                }}
              >
                <Box sx={{ mb: { xs: 2, md: 0 } }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Contacts
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Email: info@ecofusion.com
                  </Typography>
                  <Typography variant="body2">
                    Phone: +1 (555) 123-4567
                  </Typography>
                </Box>

                <Box sx={{ mb: { xs: 2, md: 0 } }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    About Us
                  </Typography>
                  <Typography variant="body2" sx={{ maxWidth: '300px' }}>
                    We're EcoFusion, harnessing AI and data to power a more
                    sustainable, eco-friendly future.
                  </Typography>
                </Box>

                <Box sx={{ mb: { xs: 2, md: 0 } }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Privacy Policy
                  </Typography>
                  <Typography variant="body2" component={Link} to="/privacy">
                    Read Here
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Careers
                  </Typography>
                  <Typography variant="body2" component={Link} to="/careers">
                    Join Our Team
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              <Typography variant="body2" sx={{ textAlign: 'center', opacity: 0.8 }}>
                © 2025 EcoFusion. All rights reserved.
              </Typography>
            </Container>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default App;
